import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from '@homeleite/arch';
import { finalize, timeout } from 'rxjs/operators';
import { currentApiEnvironment } from '../../core/api-environments';

interface WebhookRow {
    entity: string;
    status: string;
    count: number;
    last: string | null;
}

interface WebhookInfo {
    configured: boolean;
    requireSignature: boolean;
    async: boolean;
    secretHeader: string;
    associationHeader: string;
    supportedEntities: string[];
    lastReceivedAt: string | null;
    last7d: WebhookRow[];
}

interface EntitySummary {
    entity: string;
    supported: boolean;
    total: number;
    failed: number;
    last: string | null;
}

@Component({
    selector: 'app-webhook-panel',
    templateUrl: './webhook-panel.component.html',
    styleUrls: ['./webhook-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WebhookPanelComponent implements OnInit {
    state: 'loading' | 'error' | 'ready' = 'loading';
    info: WebhookInfo | null = null;
    entities: EntitySummary[] = [];
    testing = false;
    baseUrl = '';

    constructor(private api: ApiService, private cd: ChangeDetectorRef, private snackBar: MatSnackBar) {}

    ngOnInit(): void {
        this.baseUrl = currentApiEnvironment().apiRoot.replace(/\/?$/, '/') + 'public/integrations/srg-scl/webhook/';
        this.load();
    }

    load(): void {
        this.state = 'loading';
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'webhook-info')
            .getJson<WebhookInfo>()
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    this.info = r;
                    this.entities = this.summarize(r);
                    this.state = 'ready';
                    this.cd.markForCheck();
                },
                error: () => {
                    this.state = 'error';
                    this.cd.markForCheck();
                }
            });
    }

    private summarize(r: WebhookInfo): EntitySummary[] {
        const map = new Map<string, EntitySummary>();
        const get = (entity: string): EntitySummary => {
            let e = map.get(entity);
            if (!e) {
                e = { entity, supported: r.supportedEntities.includes(entity), total: 0, failed: 0, last: null };
                map.set(entity, e);
            }
            return e;
        };
        r.supportedEntities.forEach(get);
        r.last7d.forEach((row) => {
            const e = get(row.entity);
            e.total += row.count;
            if (row.status === 'FAILED') e.failed += row.count;
            if (row.last && (!e.last || row.last > e.last)) e.last = row.last;
        });
        return [...map.values()].sort((a, b) => Number(b.supported) - Number(a.supported) || b.total - a.total);
    }

    copy(text: string): void {
        const done = () => this.snackBar.open('Copiado', 'OK', { duration: 2500 });
        const fail = () => this.snackBar.open('Não foi possível copiar', 'OK', { duration: 4000 });
        if (!navigator.clipboard) {
            fail();
            return;
        }
        navigator.clipboard.writeText(text).then(done, fail);
    }

    test(): void {
        this.testing = true;
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'webhook-info', 'test')
            .postJson<{ ok: boolean }>({})
            .pipe(
                timeout(15000),
                finalize(() => {
                    this.testing = false;
                    this.cd.markForCheck();
                })
            )
            .subscribe({
                next: (r) =>
                    this.snackBar.open(r.ok ? 'Teste ok: a caixa de entrada gravou e leu o evento.' : 'Teste falhou: evento não foi gravado.', 'OK', { duration: 6000 }),
                error: () => this.snackBar.open('Teste falhou (API indisponível ou tempo esgotado)', 'OK', { duration: 6000 })
            });
    }
}
